import React from 'react';
import ListItem from './ListItem';
import styled from 'styled-components';
import {
  AuthCredential,
  reauthenticateWithCredential,
  User,
} from 'firebase/auth';
import EmailVerifiedCheckmark from '../../user/EmailVerifiedCheckmark';
import { Profile, UnsavedProfile } from '../ProfileForm';
import EmailForm from './EmailForm';
import ProfileModal from './ProfileModal';
import LoginForm from '../../../views/login/LoginForm';
import AlertBox from '../../common/AlertBox';
import { LoginContext } from '../../../views/Login';
import { LOADING_STATE } from '../../../util/loading';

interface EmailProps {
  currentUser: User;
  profile: Profile | UnsavedProfile;
  onSave: (email: string, credential: AuthCredential) => Promise<boolean>;
}

const EmailContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  line-height: 1em;
`;

const LoginFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (max-width: 480px) {
    padding: 0 24px;
  }
`;

const Email: React.FC<EmailProps> = ({ currentUser, profile, onSave }) => {
  const [mode, setMode] = React.useState<'edit' | 'reauth' | 'success'>('edit');
  const [email, setEmail] = React.useState<string>(profile.email);
  const CONTENT = {
    ['edit']: {
      title: (
        <EmailContainer>
          <span>Email</span>
          {currentUser!.emailVerified && <EmailVerifiedCheckmark />}
        </EmailContainer>
      ),
      content: (close: () => void) => (
        <EmailForm
          email={profile.email}
          onSubmit={async (values) => {
            setEmail(values.email);
            setMode('reauth');
          }}
        />
      ),
    },
    ['reauth']: {
      title: 'Reauthenticate to continue',
      content: (close: () => void) => (
        <LoginContext.Provider
          value={{
            redirectResult: { loadingState: LOADING_STATE.IDLE },
            addRedirectListener: () => {},
          }}
        >
          <LoginFormContainer>
            <LoginForm
              user={currentUser}
              reauth
              onReauth={async (credential) => {
                await reauthenticateWithCredential(currentUser, credential);
                const success = await onSave(email, credential);
                if (success) {
                  setMode('success');
                }
              }}
              enabledProviders={currentUser.providerData.map(
                (data) => data.providerId
              )}
            />
            {/* Go back button */}
          </LoginFormContainer>
        </LoginContext.Provider>
      ),
    },
    ['success']: {
      title: 'Email',
      content: (close: () => void) => (
        <>
          <AlertBox text="Email successfully updated. Please check your email for a verification link." />
          {/* Close button */}
        </>
      ),
    },
  };
  return (
    <>
      <ListItem id="email" title={CONTENT[mode].title} value={profile.email} />

      <ProfileModal
        title={CONTENT[mode].title}
        trigger="email"
        onClose={() => setMode('edit')}
        onBack={mode === 'reauth' ? () => setMode('edit') : undefined}
      >
        {({ close }) => (
          <EmailContainer>
            {CONTENT[mode].content(() => {
              close();
              // setMode('edit');
            })}
          </EmailContainer>
        )}
      </ProfileModal>
    </>
  );
};

export default Email;
